<template>
  <div id="nav-header-root" v-bind="attrsWithOverrides">
    <SkipToMainContent />
    <BaseHeader v-bind="attrsWithOverrides" />
    <MetaData />
    <MainContentStart />
    <template v-if="mounted">
      <Overlay />
      <InternationalContextSlideout />
      <AccountSlideout />
      <RegistrySlideout />
      <SearchModal />
      <google-agent-floating-entry-point
        v-if="experiments.aiFloatingActionButtonEnabled && isDomestic && !isAskAiHidden"
        trigger-selector="#ask-ai-gecx-button"
        overlap-avoid-selector=".back-to-top-button"
      />
    </template>
    <StoreSlideout v-if="isLocatorSlideoutRendered" />
    <PopupWrapper v-if="popupMediaContent" :popup-media-content="popupMediaContent" @close="closePopupMediaContent" />
  </div>
</template>

<script setup>
/* v8 ignore file -- @preserve */
import { computed, defineAsyncComponent, onBeforeUnmount, onMounted, onBeforeMount } from 'vue';
import { useMounted } from '@vueuse/core';
import { useInitExperimentation } from '#shared/features/experimentation/useInit';
import { SET_ATTRS, SET_COUNT, LOAD_USER } from '#shared/types/actions';
import { viewTypes } from '#shared/constants';
import { isSSR } from '#shared/utils/isSSR';

import { useClientInitHeader, handleViewType } from '~/composables/useInit';
import { useAttrsOverrides } from '~/composables/dumb/useAttrsOverrides';
import { useLocationUpdate } from '~/composables/useLocatorInit';
import { usePopupLink } from '~/composables/features/usePopupLink';
import useInternationalSlideout from '~/composables/features/useInternationalSlideout';
import useKillswitches from '~/composables/useKillswitches';
import useExperiments from '~/composables/useExperiments';

import onKsFlagsReady from '~/events/onKsFlagsReady';
import dispatchNavigationReady from '~/utils/dispatchNavigationReady';

import SkipToMainContent from '~/components/common/SkipToMainContent';
import MainContentStart from '~/components/common/MainContentStart';
import Overlay from '~/components/common/Overlay.common';
import MetaData from '~/components/common/MetaData.common';
import InternationalContextSlideout from '~/components/common/InternationalContextSlideout.common';
import AccountSlideout from '~/components/common/AccountSlideout.common';
import RegistrySlideout from '~/components/common/RegistrySlideout.common';
import BaseHeader from '~/components/BaseHeader';
import SearchModal from '~/components/search/SearchModal.common';
import { IS_STATE_RESTORATION_FROM_DOM_ENABLED, restoreMobileMenu, restoreDesktopMenu } from '~/utils/restoreStateFromDom';

const PopupWrapper = defineAsyncComponent(() => import(/* webpackChunkName: "PopupWrapper" */ './common/PopupWrapper'));
const StoreSlideout = defineAsyncComponent(() => import(/* webpackChunkName: "LocatorSlideout" */ './store-slideout/StoreSlideout'));

const mounted = useMounted();
const isServer = isSSR();
const pageData = usePageData();
const navHeader = useNavHeader();
const navUser = useNavUser();
const navQuickBag = useNavQuickBag();
const navPreferred = useNavPreferred();
const navViewType = useNavViewType();
const envProps = useEnvProps();
const killswitches = useKillswitches();
const experiments = useExperiments();
const isDomestic = computed(() => pageData.navigation?.context?.isDomestic);
const attrsWithOverrides = useAttrsOverrides();

const { popupMediaContent } = usePopupLink();
const { openInternationalSlideout } = useInternationalSlideout();
const navShared = useNavShared();
const isLocatorSlideoutRendered = computed(() => navPreferred.locatorSlideoutRendered);

const { isAskAiHidden } = useAskAiVisibility();

const closePopupMediaContent = () => {
  navShared[SET_POPUP_MEDIA_CONTENT](null);
};
navHeader[SET_ATTRS](attrsWithOverrides);
const { isMinimal = false } = attrsWithOverrides;

useInitExperimentation();

if (!isServer) {
  if (isMinimal) {
    handleViewType();
  } else {
    useClientInitHeader();
    useLocationUpdate();
  }
}

function handleWelcomeMatLinkClick(e) {
  const welcomeMatShipLinkSelector = '#notshipLink a';

  if (e.target.closest(welcomeMatShipLinkSelector)) {
    e.preventDefault();
    const welcomeMatModal = window.TINY && window.TINY.box;

    if (welcomeMatModal) {
      welcomeMatModal.hide();
    }

    openInternationalSlideout();
  }
}

function handleUpdateQuickBagCountEvent({ detail: count }) {
  navQuickBag[SET_COUNT](count);
}

function handleUpdateNavigationIntContextEvent({ detail }) {
  const { regionCode, currencyCode } = detail;

  if (!regionCode) return;

  const isDomestic = regionCode === 'US';

  pageData.navigation.context.isDomestic = isDomestic;
  pageData.navigation.context.isInternational = !isDomestic;
  pageData.navigation.context.regionCode = regionCode;
  pageData.navigation.context.currencyCode = currencyCode;
}

onBeforeMount(() => {
  if (envProps.isNavSSR && IS_STATE_RESTORATION_FROM_DOM_ENABLED) {
    const performanceTimerName = '[navigation-wgl] menu parse time';
    const viewType = navViewType.viewType;
    const data = navHeader.data[viewType];
    console.time(performanceTimerName);

    if (viewType === viewTypes.desktop) {
      data.desktopMenu = restoreDesktopMenu();
    }
    data.menu = restoreMobileMenu();
    console.timeEnd(performanceTimerName);
  }
});

onMounted(() => {
  if (isMinimal) return;

  onKsFlagsReady(() => {
    if (killswitches.value.internationalContextEnabled) {
      document.addEventListener('click', handleWelcomeMatLinkClick);
      window.addEventListener('updateNavigationIntContext', handleUpdateNavigationIntContextEvent);
      window.addEventListener('openNavigationIntContext', openInternationalSlideout);
    }
  });

  window.addEventListener('updateQuickBagCount', handleUpdateQuickBagCountEvent);

  dispatchNavigationReady();
  navUser[LOAD_USER]();
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleWelcomeMatLinkClick);
  window.removeEventListener('updateQuickBagCount', handleUpdateQuickBagCountEvent);
  window.removeEventListener('updateNavigationIntContext', handleUpdateNavigationIntContextEvent);
  window.removeEventListener('openNavigationIntContext', openInternationalSlideout);
});
</script>

<style lang="scss">
@import '~/assets/scss/common/reset.scss';
@import '~/assets/scss/common/skeleton-loading.scss';

.nav-visible {
  width: 100%;
  position: fixed;
}

@media only screen and (max-width: 1023px) {
  header.desktop {
    @include skeleton-loading();
  }
}

@media only screen and (min-width: 1024px) {
  header.mobile {
    @include skeleton-loading();
  }
}
</style>

<style lang="scss" brand="mcom">
@import '~/assets/scss/classes.mcom.scss';
@import '~/assets/scss/icons.mcom.scss';
</style>

<style lang="scss" brand="bcom">
@import '~/assets/scss/classes.bcom.scss';
@import '~/assets/scss/icons.bcom.scss';
</style>
